import useEventTriggered from './useEventTriggered';
import useValidation, { ERROR_TYPE } from './useValidation';
import useOnValueChange from './useOnValueChange';
import { noop } from '../util/functions';

/**
 * Hook for a common pattern of hooks when writing a text field variant 
 * @param onBlurParam {function}
 * @param onKeyDownParam {function}
 * @param onValidityChange {function}
 * @param disableValidation {boolean}
 * @param value {string}
 * @param ref {object}
 * @param overrideValidation {boolean}
 * @param errorMessageProp {string}
 * @param requiredMessage {string}
 * @param dependencies {object}
 * @param errorOnly {boolean}
 * @returns {[{
		 tooShort: boolean, 
		 valid: boolean, 
		 typeMismatch: boolean, 
		 stepMismatch: boolean, 
		 badInput: boolean, 
		 customError: boolean, 
		 rangeOverflow: boolean, 
		 rangeUnderflow: boolean, 
		 tooLong: boolean,
		 patternMismatch: boolean, 
		 valueMissing: boolean
		 }, 
		 string, 
		 function
		 ]}
 */

export default function useTextValidation({
	onBlur: onBlurParam,
	onKeyDown: onKeyDownParam,
	onValidityChange,
	disableValidation,
	value,
	ref,
	overrideValidation,
	errorMessage: errorMessageProp,
	requiredMessage,
	dependencies,
	errorOnly = true
}) {
	const [hasKeyDowned, onKeyDown] = useEventTriggered(onKeyDownParam, !disableValidation);
	const [hasBlurred, onBlur] = useEventTriggered(onBlurParam, !disableValidation);
	const [validity, errorMessage] = useValidation(
		{
			value,
			inputRef: ref,
			overrideValidation: hasBlurred ? overrideValidation : ERROR_TYPE.noValidation,
			errorMessage: errorMessageProp,
			requiredMessage,
			errorOnly
		},
		dependencies
	);

	useOnValueChange(validity.valid, valid => {
		onValidityChange(valid, validity);
	});

	return [validity, errorMessage, hasKeyDowned ? onBlur : noop, onKeyDown, hasBlurred];
}
